import Image from "next/image";
import {
  ActiveTab,
  DisplayLoadingState,
  JobStatus,
  useGenerator,
} from "@/app/context/GeneratorContext";
import {
  formatTimeAgo,
  transformGifTags,
  transformTags,
  updateTogglesList,
} from "@/lib/utils/generation";
import QueueTypeBadge from "@/components/create/generatorViewPort/queueTypeBadge";
import { useCallback, useState } from "react";
import { HistoryGeneration, HistoryJob, ModerationStatus } from "@/lib/api/job";
import JobStatusBadge from "../jobStatusBadge";
import JobTimer from "./jobTimer";
import { isSDXL } from "@/components/shared/generators/isSDXL";
import { getResolutions } from "@/lib/utils/getResolutions";
import { useGlobalContext } from "@/app/context/store";
import { getGifModel, getModel, getUserModel } from "@/lib/api/generate";
import { TagGroup } from "@/app/context/generatorStore";
import XIcon from "@/components/shared/icons/x.svg";

interface Props {
  job: HistoryJob;
  translations: any;
}

export default function JobRow({ job, translations }: Props) {
  const { user } = useGlobalContext();
  const {
    state,
    setDisplayMedia,
    setDisplayLoadingState,
    setDisplayMediaIndex,
    setActiveTab,
    setActiveJobRef,
    setGeneratingQueueType,
    refetchJobHistory,
    setCfgScale,
    setTagList,
    setCustomPrompt,
    setCustomNegative,
    setResolution,
    setBatchSize,
    setSelectedGenerator,
    batchSize,
    tagList,
    setGenerationType,
    setCustomPromptGif,
    setGifAspectRatio,
    setGifLength,
    setGifTagList,
    gifTagList,
    setTagListLoading,
    setCreditCost,
    generatorList,
    generatorListSuccess,
  } = useGenerator();
  const [tagListClone, setTagListClone] = useState(tagList);
  const [gifTagListClone, setGifTagListClone] = useState(gifTagList);

  //Reset tags back to selected:false
  const resetImageTags = useCallback(() => {
    let newTagList = tagList.map((tagSet: any) => {
      let newListOfToggles = tagSet.tags.map((tag: any) => {
        return { ...tag, selected: false };
      });
      return { ...tagSet, tags: newListOfToggles };
    });
    setTagList(newTagList);
  }, [tagList]);

  const resetGifTags = useCallback(() => {
    let newGifTagList = gifTagList.map((tagSet: any) => {
      let newListOfToggles = tagSet.tags.map((tag: any) => {
        return { ...tag, selected: false };
      });
      return { ...tagSet, tags: newListOfToggles };
    });
    setGifTagList(newGifTagList);
  }, [gifTagList]);

  //Update selected tags with new tagroups
  const updateGifTags = (gifTogglesList: TagGroup[]) => {
    const jobTagList = job.tags;
    const tempGifTogglesList = gifTogglesList || gifTagListClone;
    if (jobTagList?.length && tempGifTogglesList) {
      const jobTagSet = new Set(jobTagList);
      for (const tagGroup of tempGifTogglesList) {
        tagGroup.tags.forEach((tag: any) => {
          tag.selected = jobTagSet.has(tag.id);
        });
      }
      setGifTagList(tempGifTogglesList);
    }
  };

  const updateTags = (togglesList: TagGroup[]) => {
    const jobTagList = job.tags;
    const tempTogglesList = togglesList || tagListClone;
    if (jobTagList?.length && tempTogglesList) {
      const jobTagSet = new Set(jobTagList);
      for (const tagGroup of tempTogglesList) {
        tagGroup.tags.forEach((tag) => {
          tag.selected = jobTagSet.has(tag.id);
        });
      }
      setTagList(tempTogglesList);
    } else {
      resetImageTags();
    }
  };

  //Update tag groups when new style is selected
  const updateTagGroup = async (state: any) => {
    setTagListLoading(true);
    try {
      let model = await getModel(state._id);
      let listOfTogglesJSON = transformTags(
        model.response?.data?.tempTagGroups,
        Number(translations.index),
      );
      let userModel = await getUserModel(); // User preferences

      // If job uses hidden tag, temporarily add it back to regenerate old job
      const userHiddenTags = userModel.response?.data?.tags.hidden;
      const filterHiddenTags = userHiddenTags.filter(
        (hiddenTag: any) => !job.tags.includes(hiddenTag.tag._id),
      );
      const userSavedTags = userModel.response?.data?.tags.saved;

      // Update toggles list with user preferences from userModel
      const updatedListOfToggles = updateTogglesList(
        listOfTogglesJSON,
        userSavedTags,
        filterHiddenTags,
        Number(translations.index),
      );

      setCreditCost(model.response?.data?.creditCost);
      setTagList(updatedListOfToggles);
      setTagListClone(updatedListOfToggles);
      updateTags(updatedListOfToggles);
    } catch (error) {
      console.error("Update Tag Group:", error);
    } finally {
      setTagListLoading(false);
    }
  };

  const updateGifTagGroup = async () => {
    setTagListLoading(true);
    try {
      let gifModel = await getGifModel();
      let gifListOfTogglesJSON = transformGifTags(
        gifModel.response?.data?.tagGroups,
        Number(translations.index),
      );
      setGifTagList(gifListOfTogglesJSON);
      setGifTagListClone(gifListOfTogglesJSON);
      updateGifTags(gifListOfTogglesJSON);
    } catch (error) {
      console.error("Update Tag Group:", error);
    } finally {
      setTagListLoading(false);
    }
  };

  const handleViewJob = useCallback((job: HistoryJob) => {
    // check if job is delayed
    if (job.status === JobStatus.Delayed) {
      setActiveJobRef({ jobId: job.jobId, delayedUntil: job.delayedUntil });
      setDisplayMedia([]);
      setDisplayLoadingState(DisplayLoadingState.Delaying);
      setGeneratingQueueType(job.queueType);
    } else if (job.status === JobStatus.Pending) {
      setActiveJobRef({ jobId: job.jobId });
      setDisplayMedia([]);
      setDisplayLoadingState(DisplayLoadingState.Queueing);
      setGeneratingQueueType(job.queueType);
    } else if (job.status === JobStatus.Active) {
      setActiveJobRef({ jobId: job.jobId });
      setDisplayMedia([]);
      setDisplayLoadingState(DisplayLoadingState.Generating);
      setGeneratingQueueType(job.queueType);
    } else if (job.status === JobStatus.Completed) {
      setActiveJobRef(null);
      setDisplayMediaIndex(0);
      if (job.generationIds?.length > 0) {
        setDisplayMedia(
          job.generationIds.map((gen: HistoryGeneration) => ({
            id: gen._id,
            type: gen.videoUrl ? "gif" : "image",
            imageUrl: gen.videoUrl ? undefined : `https://cdn1.imgpog.com/${gen.unwatermarked}.jpg`,
            videoUrl: gen.videoUrl,
            thumbnailUrl: gen.thumbnailUrl,
            saved: gen.saved,
            isSaving: false,
          })),
        );
      }

      if (job.jobType === "generateVideo") {
        setGenerationType("gif");
        //Set configs
        setCustomPromptGif(job.customPrompt || "");
        setGifAspectRatio(job.aspectRatio);
        setGifLength(Number(job.length));
        //Set active GIF tags if there are tags in job
        if (job.tags.length > 0) {
          updateGifTagGroup();
        } else {
          resetGifTags();
        }
      } else {
        setGenerationType("image");

        //Update style
        const currGen = generatorList.find((gen) => gen._id === job.generator);
        if (currGen) {
          //Update tags
          updateTagGroup(currGen);
          setSelectedGenerator(currGen);
          //Set adherence level & check premium, after adherence values have been updated according to style
          setTimeout(() => {
            if (
              job.cfgScale !== currGen.frontend.cfgM &&
              (user._id !== "none" && user.membership?.level === 1 ? false : true)
            ) {
              //If adherence is not normal and not premium, set to normal
              setCfgScale(currGen.frontend.cfgM);
            } else {
              setCfgScale(job.cfgScale || currGen.frontend.cfgM);
            }
          }, 0);
        }

        //Update aspect ratio while checking if premium
        const style = currGen?.shortName;
        const isPony = isSDXL(style || "style");
        const resolutions = getResolutions(isPony);
        const currResolution = resolutions.find((res) => job.aspectRatio === res.string);
        const resValue = currResolution?.value;
        if (
          (resValue === 4 ||
            resValue === 5 ||
            resValue === 6 ||
            resValue === 8 ||
            resValue === 9) &&
          (user._id !== "none" && user.membership?.level === 1 ? false : true)
        ) {
          //Premium value with no premium membership, defaults to first option
          setResolution(isPony ? 10 : 1);
        } else {
          //Has premium membership or not a premium value
          setResolution(resValue || (isPony ? 10 : 1));
        }

        //Set the rest of the configs
        setCustomNegative(
          (user.membership?.level === 1 ? false : true) ? "" : job.customNegative || "",
        );
        setCustomPrompt(job.customPrompt || "");
        setBatchSize(job.batchSize);
      }
      setDisplayLoadingState(DisplayLoadingState.Loaded);
      window.scrollTo(0, 0);
    }
    setActiveTab(ActiveTab.Generation);
  }, []);

  const handleThumbnail = () => {
    // Delayed Jobs
    if (job.delayedUntil && new Date(job.delayedUntil) > new Date()) {
      return (
        <div className="flex h-[240px] w-full items-center justify-center border-[0.5px] border-[#3C3C3C] bg-crp-grey sm:h-[120px] sm:w-[90px]">
          <JobTimer
            delayedUntil={job.delayedUntil}
            onComplete={() => {
              refetchJobHistory();
            }}
          />
        </div>
      );
    }
    // Aborted jobs
    else if (
      job.status === JobStatus.Aborted ||
      job.moderationStatus === ModerationStatus.Rejected ||
      job.status === JobStatus.Failed
    ) {
      return (
        <div className="flex h-[240px] w-full items-center justify-center rounded border-[0.5px] border-[#3C3C3C] bg-crp-grey sm:h-[120px] sm:w-[90px]">
          <XIcon />
        </div>
      );
    }
    // Completed jobs with unwatermarked image (and not rejected)
    else if (job.status === JobStatus.Completed && job.generationIds[0]?.unwatermarked) {
      return (
        <div className="relative h-[240px] w-full rounded bg-crp-grey sm:h-[120px] sm:w-[90px]">
          <Image
            src={`https://cdn1.imgpog.com/${job.generationIds[0].unwatermarked}.jpg`}
            alt="Generated image"
            fill
            objectFit="contain"
            className="object-contain"
          />
        </div>
      );
    }
    // Completed jobs with thumbnail (e.g., video, and not rejected, no unwatermarked image)
    else if (job.status === JobStatus.Completed && job.generationIds[0]?.thumbnailUrl) {
      return (
        <div className="relative h-[240px] w-full rounded bg-crp-grey sm:h-[120px] sm:w-[90px]">
          <Image
            src={job.generationIds[0].thumbnailUrl}
            alt="Generated video"
            fill
            objectFit="contain"
            className="object-contain"
          />
        </div>
      );
    }
    // Default placeholder (for Pending, Active, or other Completed states not caught above)
    else {
      return (
        <div className="flex h-[240px] w-full items-center justify-center border-[0.5px] border-[#3C3C3C] bg-crp-grey sm:h-[120px] sm:w-[90px]">
          <Image
            src={"https://cdn6.imgpog.com/loadingGIFlight.webp"}
            width={35}
            height={35}
            alt="Generating Image"
            unoptimized
          />
        </div>
      );
    }
  };

  return (
    <div
      key={job.jobId}
      className="w-fullmin-w-[240px] cursor-pointer items-center border-t-[0.5px] border-[#3C3C3C] p-4 text-gray-400"
      onClick={() => handleViewJob(job)}
    >
      {/* Main grid container - added full width and space-between */}
      <div className="grid w-full grid-cols-1 items-center justify-between gap-4 sm:grid-cols-[auto_1fr_auto]">
        {/* Left section - Status info */}
        <div className="flex w-full min-w-[0px] flex-col items-stretch justify-start gap-2 sm:min-w-[80px]">
          <div className="w-full text-center text-sm">
            {formatTimeAgo(
              new Date(job.createdAt),
              translations.timeAgoHour,
              translations.timeAgoMin,
            )}
          </div>
          <JobStatusBadge status={job.status} translations={translations} />
          <QueueTypeBadge queueType={job.queueType} translations={translations} />
        </div>

        {/* Middle section - Thumbnail and details */}
        <div className="flex flex-col items-center gap-4 sm:grid sm:grid-cols-[90px_1fr]">
          {handleThumbnail()}
          {/* Job Details */}
          <div className="flex flex-col justify-center gap-1">
            {job.generationIds[0] && (
              <div className="text-sm">
                {job.width}x{job.height}
              </div>
            )}
            {job.generationIds.length > 0 && (
              <div className="text-sm">
                {translations.batch} x{job.generationIds.length}
              </div>
            )}
          </div>
        </div>

        {/* View Button */}
        <button
          onClick={() => handleViewJob(job)}
          className="ml-auto w-full rounded-md px-2 py-1 text-white transition-colors sm:w-auto sm:hover:text-crp-blue"
        >
          {translations.view}
        </button>
      </div>
    </div>
  );
}
